<template>
  <section>
    <div class="listview__head">
      <div>
        <div class="c-lbl">Mi perfil</div>
        <div class="listview__desc">Datos personales, contraseña, seguridad y sesiones activas.</div>
      </div>
    </div>

    <!-- Datos de la cuenta -->
    <div class="card card-p mb-16">
      <div class="c-lbl mb-8">Datos de la cuenta</div>
      <form @submit.prevent="saveProfile" class="form-grid">
        <div class="form-row form-row--2">
          <div>
            <label class="fl">Nombre</label>
            <input class="fi" v-model="profile.name" required maxlength="120" />
          </div>
          <div>
            <label class="fl">Email</label>
            <input class="fi" v-model="profile.email" type="email" required maxlength="160" />
          </div>
        </div>
        <div v-if="profile.error" class="form-error">{{ profile.error }}</div>
        <div v-if="profile.ok" class="form-ok">{{ profile.ok }}</div>
        <div>
          <button class="btn btn-p" type="submit" :disabled="profile.saving">
            {{ profile.saving ? 'Guardando...' : 'Guardar cambios' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Cambiar contraseña -->
    <div class="card card-p mb-16">
      <div class="c-lbl mb-8">Cambiar contraseña</div>
      <form @submit.prevent="changePassword" class="form-grid">
        <div>
          <label class="fl">Contraseña actual</label>
          <input class="fi" type="password" v-model="pwd.current" autocomplete="current-password" required />
        </div>
        <div class="form-row form-row--2">
          <div>
            <label class="fl">Nueva contraseña</label>
            <input class="fi" type="password" v-model="pwd.next" autocomplete="new-password" minlength="8" required />
          </div>
          <div>
            <label class="fl">Confirmar nueva</label>
            <input class="fi" type="password" v-model="pwd.confirm" autocomplete="new-password" minlength="8" required />
          </div>
        </div>
        <div v-if="pwd.error" class="form-error">{{ pwd.error }}</div>
        <div v-if="pwd.ok" class="form-ok">{{ pwd.ok }}</div>
        <div>
          <button class="btn btn-p" type="submit" :disabled="pwd.saving">
            {{ pwd.saving ? 'Actualizando...' : 'Actualizar contraseña' }}
          </button>
        </div>
      </form>
    </div>

    <!-- 2FA -->
    <div class="card card-p mb-16">
      <div class="c-lbl mb-8">Seguridad — Autenticación en 2 pasos</div>
      <div v-if="loading" class="tbl-empty">Cargando...</div>

      <div v-else-if="!twoFa.enabled && !twoFa.pending">
        <p style="color:var(--tx-md);font-size:13px;margin-bottom:14px">
          Agregá una capa extra de seguridad con una app como Google Authenticator, Authy o 1Password.
        </p>
        <button class="btn btn-p" @click="enable2fa" :disabled="busy">
          {{ busy ? 'Preparando...' : 'Activar 2FA' }}
        </button>
      </div>

      <div v-else-if="twoFa.pending">
        <p style="color:var(--tx-md);font-size:13px;margin-bottom:10px">
          Escaneá el QR con tu app TOTP y confirmá con un código de 6 dígitos.
        </p>
        <div style="display:flex;gap:20px;flex-wrap:wrap;align-items:center">
          <div v-html="twoFa.qr_svg" class="qr-box"></div>
          <div style="flex:1;min-width:220px">
            <div style="font-size:11.5px;color:var(--tx-lo);margin-bottom:4px">O ingresá manualmente:</div>
            <code class="qr-secret">{{ twoFa.secret }}</code>
            <form @submit.prevent="confirm2fa" style="margin-top:14px;display:flex;gap:8px;align-items:flex-end;flex-wrap:wrap">
              <div style="flex:1;min-width:140px">
                <label class="fl">Código de 6 dígitos</label>
                <input class="fi" v-model="twoFa.code" maxlength="6" inputmode="numeric" autocomplete="one-time-code" placeholder="123456" />
              </div>
              <button class="btn btn-p" type="submit" :disabled="busy || twoFa.code.length !== 6">
                {{ busy ? 'Verificando...' : 'Confirmar' }}
              </button>
            </form>
            <p v-if="twoFa.error" class="form-error" style="margin-top:8px">{{ twoFa.error }}</p>
          </div>
        </div>
      </div>

      <div v-else-if="twoFa.enabled">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap">
          <div>
            <span class="pill pill-g">2FA activo ✓</span>
            <p style="color:var(--tx-md);font-size:12.5px;margin:8px 0 0">Ya tenés 2FA configurado en esta cuenta.</p>
          </div>
          <button class="btn btn-g" @click="twoFa.disableOpen = true">Desactivar 2FA</button>
        </div>

        <div v-if="twoFa.recovery_codes?.length" class="rec-codes">
          <strong>Guardá estos códigos de recuperación en un lugar seguro:</strong>
          <ul>
            <li v-for="c in twoFa.recovery_codes" :key="c">{{ c }}</li>
          </ul>
          <p style="font-size:11.5px;color:var(--tx-lo);margin-top:6px">Cada uno solo se puede usar una vez. Esta es la única vez que los ves.</p>
        </div>
      </div>
    </div>

    <!-- Sesiones activas -->
    <div class="card card-p mb-16">
      <div class="c-lbl mb-8">Sesiones activas</div>
      <div v-if="tokens.loading" class="tbl-empty">Cargando...</div>
      <div v-else-if="!tokens.items.length" class="tbl-empty">Sin sesiones.</div>
      <table v-else class="tbl">
        <thead>
          <tr><th>Nombre</th><th>Creada</th><th>Último uso</th><th style="width:140px"></th></tr>
        </thead>
        <tbody>
          <tr v-for="t in tokens.items" :key="t.id">
            <td>
              <strong style="color:var(--tx-hi)">{{ t.name }}</strong>
              <span v-if="t.current" class="pill pill-b" style="margin-left:6px">Actual</span>
            </td>
            <td class="mono">{{ fmtDate(t.created_at) }}</td>
            <td class="mono">{{ t.last_used_at ? fmtDate(t.last_used_at) : '—' }}</td>
            <td class="tbl-actions">
              <button class="btn btn-g btn-sm" :disabled="t.current" @click="revokeToken(t)">Revocar</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <Modal v-model="twoFa.disableOpen" title="Desactivar 2FA" :max-width="420">
      <p style="color:var(--tx-md);font-size:13px">Ingresá tu contraseña para desactivar la autenticación en 2 pasos.</p>
      <input class="fi" style="margin-top:10px" type="password" v-model="twoFa.disablePwd" placeholder="Contraseña" autocomplete="current-password" />
      <p v-if="twoFa.disableError" class="form-error" style="margin-top:8px">{{ twoFa.disableError }}</p>
      <template #footer>
        <button class="btn btn-g" @click="twoFa.disableOpen = false">Cancelar</button>
        <button class="btn btn-p" style="background:linear-gradient(135deg,#d45a5a,#b03a3a)" @click="disable2fa" :disabled="busy">
          {{ busy ? 'Desactivando...' : 'Desactivar' }}
        </button>
      </template>
    </Modal>
  </section>
</template>

<script>
import api from '../services/api';
import Modal from '../components/Modal.vue';
import { auth, fetchMe } from '../services/auth';

export default {
  name: 'ProfileView',
  components: { Modal },
  data() {
    return {
      loading: true, busy: false,
      profile: { name: '', email: '', saving: false, error: '', ok: '' },
      pwd:     { current: '', next: '', confirm: '', saving: false, error: '', ok: '' },
      twoFa: {
        enabled: false, pending: false,
        qr_svg: '', secret: '', code: '', recovery_codes: null,
        disableOpen: false, disablePwd: '', disableError: '',
        error: ''
      },
      tokens: { items: [], loading: false }
    };
  },
  computed: { user() { return auth.user; } },
  async mounted() {
    this.profile.name  = this.user?.name  || '';
    this.profile.email = this.user?.email || '';
    await Promise.all([this.loadStatus(), this.loadTokens()]);
  },
  methods: {
    fmtDate(s) {
      if (!s) return '—';
      const d = new Date(s);
      return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    async saveProfile() {
      this.profile.saving = true; this.profile.error = ''; this.profile.ok = '';
      try {
        await api.put('/profile', { name: this.profile.name, email: this.profile.email });
        await fetchMe();
        this.profile.ok = 'Datos actualizados.';
      } catch (e) {
        this.profile.error = e?.response?.data?.message || Object.values(e?.response?.data?.errors ?? {}).flat()[0] || 'No se pudo guardar.';
      } finally { this.profile.saving = false; }
    },
    async changePassword() {
      this.pwd.saving = true; this.pwd.error = ''; this.pwd.ok = '';
      if (this.pwd.next !== this.pwd.confirm) {
        this.pwd.error = 'La confirmación no coincide.';
        this.pwd.saving = false;
        return;
      }
      try {
        await api.put('/profile/password', {
          current_password: this.pwd.current,
          password: this.pwd.next,
          password_confirmation: this.pwd.confirm
        });
        this.pwd.ok = 'Contraseña actualizada. Las demás sesiones fueron cerradas.';
        this.pwd.current = this.pwd.next = this.pwd.confirm = '';
        await this.loadTokens();
      } catch (e) {
        this.pwd.error = e?.response?.data?.message || Object.values(e?.response?.data?.errors ?? {}).flat()[0] || 'No se pudo actualizar.';
      } finally { this.pwd.saving = false; }
    },
    async loadStatus() {
      this.loading = true;
      try {
        const { data } = await api.get('/2fa/status');
        this.twoFa.enabled = !!data.enabled;
      } catch (e) { if (!e?.isAuthExpired) console.warn('[2FA status]', e); }
      finally { this.loading = false; }
    },
    async enable2fa() {
      this.busy = true; this.twoFa.error = '';
      try {
        const { data } = await api.post('/2fa/enable');
        this.twoFa.pending = true;
        this.twoFa.qr_svg  = data.qr_svg;
        this.twoFa.secret  = data.secret;
        this.twoFa.code    = '';
      } catch (e) {
        this.twoFa.error = e?.response?.data?.message || 'No se pudo iniciar 2FA.';
      } finally { this.busy = false; }
    },
    async confirm2fa() {
      this.busy = true; this.twoFa.error = '';
      try {
        const { data } = await api.post('/2fa/confirm', { code: this.twoFa.code });
        this.twoFa.enabled = true;
        this.twoFa.pending = false;
        this.twoFa.recovery_codes = data.recovery_codes || [];
        await fetchMe();
      } catch (e) {
        this.twoFa.error = e?.response?.data?.message || 'Código inválido.';
      } finally { this.busy = false; }
    },
    async disable2fa() {
      this.busy = true; this.twoFa.disableError = '';
      try {
        await api.post('/2fa/disable', { password: this.twoFa.disablePwd });
        this.twoFa.enabled = false;
        this.twoFa.recovery_codes = null;
        this.twoFa.disableOpen = false;
        this.twoFa.disablePwd = '';
        await fetchMe();
      } catch (e) {
        this.twoFa.disableError = e?.response?.data?.message || 'No se pudo desactivar.';
      } finally { this.busy = false; }
    },
    async loadTokens() {
      this.tokens.loading = true;
      try {
        const { data } = await api.get('/profile/tokens');
        this.tokens.items = data.data || [];
      } catch (e) { if (!e?.isAuthExpired) console.warn('[tokens]', e); }
      finally { this.tokens.loading = false; }
    },
    async revokeToken(t) {
      if (t.current) return;
      try {
        await api.delete(`/profile/tokens/${t.id}`);
        await this.loadTokens();
      } catch (e) {
        console.warn('[revoke]', e);
      }
    }
  }
};
</script>

<style scoped>
.fi.fi--ro{ background:var(--acc-xlt); color:var(--tx-hi); cursor:default; }
.qr-box{ padding:8px; background:#fff; border:1px solid var(--border); border-radius:10px; display:inline-block; }
.qr-box :deep(svg){ display:block; width:160px; height:160px; }
.qr-secret{
  display:inline-block; padding:6px 10px; border-radius:6px;
  background:var(--acc-xlt); border:1px solid var(--acc-lt);
  font-family:'SF Mono','Consolas',monospace; font-size:12px;
  color:var(--tx-hi); letter-spacing:.04em; word-break:break-all;
}
.rec-codes{ margin-top:16px; padding:12px 14px; background:var(--mhlm-pastel-sand); border-radius:8px; }
.rec-codes strong{ display:block; margin-bottom:8px; color:#8a5a10; font-size:12.5px; }
.rec-codes ul{ columns:2; column-gap:18px; list-style:none; padding:0; margin:0;
  font-family:'SF Mono','Consolas',monospace; font-size:12.5px; color:var(--tx-hi); }
.rec-codes li{ padding:2px 0; }
.form-ok{ color:#2a7a3a; font-size:12.5px; background:#e5f6e9; padding:8px 12px; border-radius:6px; }
</style>
